import React, { useMemo, useState } from "react";
import styles from "./StartScreen.module.css";
import { Button, ModestButton } from "./components/Button";
import {
  clearHistory,
  getActivityHistory,
  getCharacterHistory,
} from "./history";
import { Locale, locales } from "./data";
import { SettingIcon } from "./icons";

const isOnHomeScreen = (): boolean => {
  // Based on: https://github.com/rajatsehgal/add-to-home-screen/blob/340d37d48489b0f5653a581be35ed999a90a387a/AddToHomeScreen.js
  // License: MIT
  const { userAgent } = window.navigator;
  const iOS = !!userAgent.match(/iPad/i) || !!userAgent.match(/iPhone/i);
  const webkit = !!userAgent.match(/WebKit/i);
  const iOSSafari = iOS && webkit && !userAgent.match(/CriOS/i);

  if (iOSSafari && (window.navigator as any).standalone) {
    return true;
  }

  return window.matchMedia("(display-mode: standalone)").matches;
};

export const StartScreen: React.FC<{
  locale: Locale;
  onChangeLocale: (locale: Locale) => void;
  mode: "character" | "activity" | "both";
  onChangeMode: (newMode: "character" | "activity" | "both") => void;
  selectedCategoryNames: string[];
  onToggleCategory: (categoryName: string) => void;
  onStart: () => void;
}> = ({
  locale,
  onStart,
  onChangeLocale,
  onChangeMode,
  mode,
  selectedCategoryNames,
  onToggleCategory,
}) => {
  const [, setCounter] = useState(0);

  const totalCount = useMemo(() => {
    const amount = locale.categories.reduce(
      (acc, category) => acc + category.characters.length,
      0
    );
    const rounded = (Math.floor(amount / 100) * 100).toLocaleString(
      locale.localeCode
    );
    return rounded;
  }, [locale]);

  return (
    <div className={styles.container}>
      <div className={styles.small}>
        <img
          src="/logo-160x160.png"
          alt="Impersonate.app"
          width={80}
          height={80}
        />
      </div>

      <div className={styles.languageButtons}>
        <button
          className={`${styles.languageButton} ${
            locale.localeCode === "en" ? styles.languageButtonActive : ""
          }`}
          onClick={() => onChangeLocale(locales.en)}
        >
          🇬🇧
        </button>
        <button
          className={`${styles.languageButton} ${
            locale.localeCode === "fi" ? styles.languageButtonActive : ""
          }`}
          onClick={() => onChangeLocale(locales.fi)}
        >
          🇫🇮
        </button>
      </div>

      <div className={styles.stack0}>
        <div className={styles.greySmall}>{locale.text.howToPlay1}</div>
        <div>{locale.text.howToPlay2}</div>
      </div>

      <div className={styles.stack0}>
        <div className={styles.greySmall}>{locale.text.youWillGet1}</div>
        <div>{locale.text.youWillGet2}</div>
      </div>

      <div className={styles.stack0}>
        <div className={styles.greySmall}>{locale.text.andAnActivity1}</div>
        <div>{locale.text.andAnActivity2}</div>
      </div>

      <div className={styles.stack0}>
        <div className={styles.greySmall}>
          {locale.text.impersonateTheCombination1}
        </div>
        <div>{locale.text.impersonateTheCombination2}</div>
      </div>

      <div className={styles.stack0}>
        <div className={styles.greySmall}>{locale.text.passThePhone1}</div>
        <div>{locale.text.passThePhone2}</div>
      </div>

      <div className={styles.stack8}>
        <div className={styles.greySmall}>{locale.text.otherWaysToPlay1}</div>
        <div className={styles.small}>{locale.text.otherWaysToPlay2}</div>
        <div className={styles.small}>{locale.text.otherWaysToPlay3}</div>
        <div className={styles.small}>{locale.text.otherWaysToPlay4}</div>
      </div>

      <div className={styles.stack8}>
        <div className={styles.greySmall}>{locale.text.keepItFun1}</div>
        <div className={styles.small}>
          {locale.text.keepItFun2.replace("{totalCount}", totalCount)}
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.stack8}>
        <div className={styles.greySmall}>
          {locale.text.mode1} <SettingIcon />
        </div>
        {(
          [
            { label: locale.text.mode2, value: "character" },
            { label: locale.text.mode3, value: "activity" },
            { label: locale.text.mode4, value: "both" },
          ] as const
        ).map((item, itemIndex) => (
          <button
            key={itemIndex}
            className={`${styles.settingButton} ${
              mode === item.value ? styles.settingButtonActive : ""
            }`}
            onClick={() => onChangeMode(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className={styles.stack8}>
        <div className={styles.greySmall}>
          {locale.text.categories1} <SettingIcon />
        </div>
        {locale.categories.map((category, categoryIndex) => (
          <button
            key={categoryIndex}
            className={`${styles.settingButton} ${
              selectedCategoryNames.includes(category.name)
                ? styles.settingButtonActive
                : ""
            }`}
            onClick={() => onToggleCategory(category.name)}
          >
            {category.name}
          </button>
        ))}
      </div>

      <div className={styles.stack8}>
        <Button onClick={onStart}>{locale.text.start1}</Button>
        {!isOnHomeScreen() && (
          <div className={styles.greySmall}>{locale.text.start2}</div>
        )}
      </div>

      {(getCharacterHistory().length > 0 ||
        getActivityHistory().length > 0) && (
        <div className={styles.stack8}>
          <ModestButton
            onClick={() => {
              clearHistory();
              setCounter((prevValue) => prevValue + 1);
            }}
          >
            {locale.text.clearHistory1}
          </ModestButton>
          <div className={styles.greySmall}>{locale.text.clearHistory2}</div>
        </div>
      )}
    </div>
  );
};
